import { Link } from 'react-router';
import { useId, useState } from 'react';
import { Button, Dialog, Field, useToast } from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { formatRM } from '@/features/billing/money';
import { useCancelLabAppBooking } from '@/features/lab-packages/api';
import {
  CANCEL_REASON_LIMITS,
  type LabAppBooking,
} from '@/features/lab-packages/types';
import { isApiError } from '@/lib/api';
import { whenLabel } from '@/pages/calendar/time';
import styles from '@/pages/queue/queue.module.css';
import { LabFailureBox } from '@/pages/setup/LabFailureBox';
import { toFailure, type Failure } from '@/pages/setup/lab-package-format';
import own from './AppBookings.module.css';

/** Codes that send the desk to Billing first. */
const BILLING_CODES = new Set(['LAB_BOOKING_PAID', 'LAB_BOOKING_BILL_CLOSED']);

/**
 * Cancel a blood test booked in the app (POST /lab/app-bookings/{id}/cancel
 * {version, reason}): the unpaid bill is voided, a branch visit cancelled, a
 * package order withdrawn and a doctor's order back to clinic collection. The
 * patient gets one notification (never the reason).
 */
export function CancelAppBookingDialog({
  booking: b,
  onClose,
}: {
  booking: LabAppBooking;
  onClose: () => void;
}) {
  const formId = useId();
  const toast = useToast();
  const { hasPermission } = useSession();
  const cancel = useCancelLabAppBooking();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string>();
  const [failure, setFailure] = useState<Failure | null>(null);
  const [billing, setBilling] = useState(false);
  const open = b.status === 'booked';

  async function submit() {
    setFailure(null);
    const text = reason.trim();
    if (text.length < CANCEL_REASON_LIMITS.min)
      return setError('Enter why the clinic cancels (at least 3 characters).');
    try {
      await cancel.mutateAsync({ id: b.id, version: b.version, reason: text });
      toast.show({
        tone: 'success',
        title: `Booking cancelled: ${b.patient.fullName} is notified in the app`,
      });
      onClose();
    } catch (err) {
      if (isApiError(err) && err.fieldError('reason'))
        return setError(err.fieldError('reason'));
      setFailure(toFailure(err));
      setBilling(isApiError(err) && BILLING_CODES.has(err.code));
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      dismissible={false}
      title={`Cancel the app booking · ${b.patient.fullName}`}
      footer={
        <>
          <Button onClick={onClose} disabled={cancel.isPending}>
            Keep booking
          </Button>
          <Button
            type="submit"
            form={formId}
            variant="danger"
            loading={cancel.isPending}
            disabled={!open}
          >
            Cancel booking
          </Button>
        </>
      }
    >
      <form
        id={formId}
        className={styles.drawer}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {failure && <LabFailureBox failure={failure} />}
        {billing && (
          <p className={styles.infoNote}>
            {hasPermission('billing:read') ? (
              <>
                Settle the bill in <Link to="/billing">Billing</Link>, then
                cancel the booking here.
              </>
            ) : (
              'Ask the counter to settle the bill in Billing, then cancel the booking here.'
            )}
          </p>
        )}
        <dl className={styles.rows}>
          <dt>Tests</dt>
          <dd>{b.package?.name ?? 'Tests ordered by a doctor'}</dd>
          <dt>When</dt>
          <dd>{whenLabel(b.startsAt)}</dd>
          <dt>Where</dt>
          <dd>
            {b.mode === 'home'
              ? `Home visit${b.homeAddress ? `: ${b.homeAddress}` : ''}`
              : `At ${b.branch.name}`}
          </dd>
          {b.priceSen !== null && (
            <>
              <dt>Price</dt>
              <dd>
                {formatRM(b.priceSen)}
                {b.homeFeeSen !== null &&
                  ` + ${formatRM(b.homeFeeSen)} home visit`}
              </dd>
            </>
          )}
        </dl>
        <p className={styles.warnNote}>
          The unpaid bill is voided
          {b.mode === 'clinic' ? ' and the branch visit cancelled' : ''}.{' '}
          {b.kind === 'package'
            ? 'The package’s lab order is withdrawn.'
            : 'The doctor’s order goes back to collection at the clinic, to book again later.'}{' '}
          The patient is notified in the app; the reason is not shown to them.
        </p>
        {!open && (
          <p className={styles.errorNote} role="status">
            This booking is already cancelled.
          </p>
        )}
        <Field
          label="Reason"
          hint="Kept with the booking for staff. Never shown to the patient."
          error={error}
        >
          <textarea
            className={own.textarea}
            value={reason}
            maxLength={CANCEL_REASON_LIMITS.max}
            disabled={!open}
            onChange={(e) => {
              setReason(e.target.value);
              setError(undefined);
            }}
          />
        </Field>
      </form>
    </Dialog>
  );
}
